import React, { useState, useEffect, useCallback } from "react";
import "./TravelPassport.css";
import ModalBox from "../../components/ModalBox/ModalBox";
import { initialModal } from "./Modals";
import { trackGameStart } from "../../utils/trackEvents";

const regionSubregions = {
  "North America": ["North America", "Central America", "Caribbean"],
  "South America": ["South America"],
  Europe: [
    "Northern Europe",
    "Western Europe",
    "Eastern Europe",
    "Southern Europe",
  ],
  Asia: [
    "Central Asia",
    "Eastern Asia",
    "South-Eastern Asia",
    "Southern Asia",
    "Western Asia",
  ],
  Africa: [
    "Northern Africa",
    "Western Africa",
    "Middle Africa",
    "Eastern Africa",
    "Southern Africa",
  ],
  Oceania: [
    "Australia and New Zealand",
    "Melanesia",
    "Micronesia",
    "Polynesia",
  ],
};

const Stamp = React.memo(({ name, flag, rotation, onRemove }) => (
  <div className="stamp" style={{ "--rot": `${rotation}deg` }}>
    {flag && <img src={flag} alt={`${name} flag`} className="stamp-flag" />}
    <span className="stamp-label">{name}</span>
    <button className="stamp-remove" onClick={() => onRemove(name)}>
      ×
    </button>
  </div>
));

export default function TravelPassport() {
  const [allCountries, setAllCountries] = useState([]);
  const [visited, setVisited] = useState([]);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [modalContent] = useState(initialModal());

  useEffect(() => {
    fetch("https://restcountries.com/v3.1/all")
      .then((res) => res.json())
      .then((data) => setAllCountries(data.map((c) => c.name.common).sort()))
      .catch(console.error);
  }, []);

  const handleAdd = useCallback(
    async (name) => {
      if (
        !name ||
        visited.some((v) => v.name.toLowerCase() === name.toLowerCase())
      )
        return;
      let flag = null,
        region = null,
        subregion = null;
      try {
        const res = await fetch(
          `https://restcountries.com/v3.1/name/${encodeURIComponent(
            name
          )}?fullText=true`
        );
        const country = (await res.json())[0];
        flag =
          country?.flags?.png ||
          country?.flags?.svg ||
          "https://flagcdn.com/w320/un.png";
        region = country.region;
        subregion = country.subregion;
        name = country.name.common;
      } catch {}
      setVisited((prev) => [...prev, { name, flag, region, subregion }]);
      setInput("");
      setSuggestions([]);
    },
    [visited]
  );

  const updateSuggestions = (val) => {
    setInput(val);
    if (!val) return setSuggestions([]);
    const matches = allCountries
      .filter((c) => c.toLowerCase().startsWith(val.toLowerCase()))
      .slice(0, 5);
    setSuggestions(matches);
  };

  const handleRemove = useCallback((name) => {
    setVisited((prev) => prev.filter((v) => v.name !== name));
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    trackGameStart("TravelPassport", 1);
  };

  return (
    <div className="travel-passport-main">
      <ModalBox
        isOpen={isModalOpen}
        title={modalContent.title}
        description={modalContent.description}
        buttonText={modalContent.buttonText}
        buttonType={modalContent.buttonType}
        onClose={closeModal}
      />
      {!isModalOpen && (
        <>
          <div className="travel-passport-countries-search">
            <div className="passport-form">
              <input
                placeholder="Enter country name"
                value={input}
                onChange={(e) => updateSuggestions(e.target.value)}
              />
              <button onClick={() => handleAdd(input)}>Stamp</button>
              {suggestions.length > 0 && (
                <ul className="suggestions">
                  {suggestions.map((s) => (
                    <li
                      key={s}
                      onClick={() => {
                        setInput(s);
                        setSuggestions([]);
                      }}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="travel-passport-content">
            <div className="travel-passport-countries-stamps">
              {visited.map((v, i) => (
                <Stamp
                  key={v.name}
                  name={v.name}
                  flag={v.flag}
                  rotation={i % 2 === 0 ? -1.5 : 1.5}
                  onRemove={handleRemove}
                />
              ))}
            </div>

            <div className="travel-passport-checklist-badges">
              {[
                { id: "North America", label: "Visited North America" },
                { id: "South America", label: "Visited South America" },
                { id: "Europe", label: "Visited Europe" },
                { id: "Asia", label: "Visited Asia" },
                { id: "Africa", label: "Visited Africa" },
                { id: "Oceania", label: "Visited Oceania" },
                { id: "2", label: "Visited 2 Countries" },
                { id: "5", label: "Visited 5 Countries" },
                { id: "10", label: "Visited 10 Countries" },
              ].map((badge) => {
                const count =
                  ["2", "5", "10"].includes(badge.id) &&
                  visited.length >= +badge.id;
                const region =
                  regionSubregions[badge.id] &&
                  visited.some(
                    (v) =>
                      v.subregion &&
                      regionSubregions[badge.id].includes(v.subregion)
                  );
                const unlocked = count || region;
                return (
                  <div
                    key={badge.id}
                    className={`badge ${
                      unlocked ? "badge-unlocked" : "badge-locked"
                    }`}
                  >
                    {badge.label}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
